<template>
    <b-container style="height: 100%" v-if="wizardData" fluid>
        <b-row class="mt-3">
            <b-col>
                <b>{{ wizardData.name }} - Additional Info</b>
            </b-col>
        </b-row>
        <hr />
        <div class="mb-4">
            <grid
                v-if="isFetched"
                :column-defs="cancellationPolicyColumnDefs"
                :row-data="additionalInfo.cancellation_policy"
                :header-height="50"
                :is-search="false"
                :is-auto-width="true"
                :is-auto-height="true"
                :suppress-row-click-selection="true"
                grid-theme="ag-theme-alpine-property-setup"
            >
                <h5 class="table-header"><b>Cancellation Policy:</b></h5>
                <responsive-button
                    class="ml-1"
                    variant="light"
                    size="sm"
                    :icon="['fas', 'pen-to-square']"
                    href="/app/policy/cancellation-policy/list/"
                >
                    Edit
                </responsive-button>
            </grid>
        </div>
        <div class="mb-4">
            <grid
                v-if="isFetched"
                :column-defs="taxesColumnDefs"
                :row-data="additionalInfo.taxes"
                :header-height="50"
                :is-search="false"
                :is-auto-width="true"
                :is-auto-height="true"
                :suppress-row-click-selection="true"
                grid-theme="ag-theme-alpine-property-setup"
            >
                <h5 class="table-header"><b>Taxes:</b></h5>
                <responsive-button
                    class="ml-1"
                    variant="light"
                    size="sm"
                    :icon="['fas', 'pen-to-square']"
                    href="/app/property-manager/tax/"
                >
                    Edit
                </responsive-button>
            </grid>
        </div>
        <div class="mb-4">
            <grid
                v-if="isFetched"
                :column-defs="feesColumnDefs"
                :row-data="additionalInfo.fees"
                :header-height="50"
                :is-search="false"
                :is-auto-width="true"
                :is-auto-height="true"
                :suppress-row-click-selection="true"
                grid-theme="ag-theme-alpine-property-setup"
            >
                <h5 class="table-header"><b>Fees:</b></h5>
                <responsive-button
                    class="ml-1"
                    variant="light"
                    size="sm"
                    :icon="['fas', 'pen-to-square']"
                    href="/app/property-manager/fee/"
                >
                    Edit
                </responsive-button>
            </grid>
        </div>
        <div class="mb-4">
            <grid
                v-if="isFetched"
                :column-defs="depositsColumnDefs"
                :row-data="additionalInfo.other_deposits"
                :header-height="50"
                :is-search="false"
                :is-auto-width="true"
                :is-auto-height="true"
                :suppress-row-click-selection="true"
                grid-theme="ag-theme-alpine-property-setup"
            >
                <h5 class="table-header"><b>Deposits:</b></h5>
                <responsive-button
                    class="ml-1"
                    variant="light"
                    size="sm"
                    :icon="['fas', 'pen-to-square']"
                    href="/app/property-manager/security-deposit/"
                >
                    Edit
                </responsive-button>
            </grid>
        </div>
        <div class="mb-4">
            <grid
                v-if="isFetched"
                :column-defs="servicesColumnDefs"
                :row-data="additionalInfo.services"
                :header-height="50"
                :is-search="false"
                :is-auto-width="true"
                :is-auto-height="true"
                :saveRowOrder="saveRowOrder"
                :enableCellTextSelection="false"
                :suppress-row-click-selection="true"
                grid-theme="ag-theme-alpine-property-setup"
            >
                <h5 class="table-header"><b>Services:</b></h5>
                <responsive-button
                    class="ml-1"
                    variant="light"
                    size="sm"
                    :icon="['fas', 'pen-to-square']"
                    href="/app/property-manager/service/"
                >
                    Edit
                </responsive-button>
            </grid>
        </div>
        <div class="pb-5">
            <grid
                v-if="isFetched"
                :column-defs="discountsColumnDefs"
                :row-data="additionalInfo.discounts"
                :header-height="50"
                :is-search="false"
                :is-auto-width="true"
                :is-auto-height="true"
                :suppress-row-click-selection="true"
                grid-theme="ag-theme-alpine-property-setup"
            >
                <h5 class="table-header"><b>Discounts:</b></h5>
                <responsive-button
                    class="ml-1"
                    variant="light"
                    size="sm"
                    :icon="['fas', 'pen-to-square']"
                    href="/app/property-manager/discount/"
                >
                    Edit
                </responsive-button>
            </grid>
        </div>
    </b-container>
</template>

<script>
import API from '../../../../api';
import { mapGetters } from 'vuex';
import Grid from '../../../generic/grid/grid.vue';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import { removeScrollMixin } from '../../../../mixins/remove-scroll-mixin';
import { RENTAL_PROPERTY_SETUP_ADDITIONAL_ENDPOINT } from '../../../../endpoints';
import { toAmountDisplay } from '../../../../utils/numbers';

export default {
    components: { Grid, ResponsiveButton },
    mixins: [removeScrollMixin],
    inject: ['propertyId'],
    data() {
        return {
            isFetched: false,
            additionalInfo: {
                discounts: [],
                fees: [],
                other_deposits: [],
                cancellation_policy: [],
                services: [],
                taxes: [],
            },
            sortableOptions: {
                stop: '',
                'ui-floating': true,
                revert: false,
                tolerance: 'pointer',
                cursor: 'move',
            },
        };
    },
    mounted() {
        this.fetchAdditionalInfo();
    },
    computed: {
        ...mapGetters('setupPropertyStore', [
            'wizardData',
            'initialData',
            'additionalData',
        ]),
        cancellationPolicyColumnDefs() {
            return [
                {
                    field: 'template.name',
                    headerName: 'Cancellation Policy Name',
                    autoWidthExclude: true,
                },
                {
                    field: 'cancellation_fee',
                    headerName: 'Cancellation Fee',
                    type: 'amountColumn',
                    minWidth: 145,
                    maxWidth: 145,
                },
                {
                    field: 'description',
                    headerName: 'Description',
                    autoHeight: true,
                    cellRenderer: ({ data }) => {
                        return data.periods ? data.periods.map(
                                        (item) =>
                                            `${toAmountDisplay({
                                                amount: item.refund_percent,
                                                percentage: true,
                                            })} refund if ${item.nights_before_checkin} days before arrival`
                                    )
                                    .join('<br/>')
                            : '';
                    },
                },
            ];
        },
        taxesColumnDefs() {
            return [
                {
                    field: 'title',
                    headerName: 'Tax Name',
                    autoWidthExclude: true,
                },
                {
                    field: 'tax_type',
                    headerName: 'Type',
                },
                {
                    field: 'display_frequency',
                    headerName: 'Frequency',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                    minWidth: 125,
                },
                {
                    field: 'display_value',
                    headerName: 'Value',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                },
            ];
        },
        feesColumnDefs() {
            return [
                {
                    field: 'title',
                    headerName: 'Fee Name',
                    autoWidthExclude: true,
                    minWidth: 150,
                },
                {
                    field: 'fee_type',
                    headerName: 'Type',
                },
                {
                    field: 'fee_group',
                    headerName: 'Group',
                },
                {
                    field: 'display_frequency',
                    headerName: 'Frequency',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                    minWidth: 125,
                },
                {
                    field: 'display_value',
                    headerName: 'Value',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                },
            ];
        },
        depositsColumnDefs() {
            return [
                {
                    field: 'title',
                    headerName: 'Other Deposit Name',
                    autoWidthExclude: true,
                },
                {
                    field: 'pre_auth',
                    headerName: 'Pre-Auth?',
                },
                {
                    field: 'display_value',
                    headerName: 'Value',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                },
            ];
        },
        servicesColumnDefs() {
            return [
                {
                    field: 'name',
                    headerName: 'Service Name',
                    rowDrag: this.isMediumScreen,
                    cellClass: 'first-drag-column',
                    rowDragText: ({ defaultTextValue }) => defaultTextValue,
                    autoWidthExclude: true,
                },
                {
                    field: 'display_value',
                    headerName: 'Value',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                    rowDrag: this.isMediumScreen,
                    rowDragText: ({ rowNode }) => rowNode.data.name,
                },
                {
                    field: 'time_type',
                    headerName: 'Billing',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                    rowDrag: this.isMediumScreen,
                    rowDragText: ({ rowNode }) => rowNode.data.name,
                },
            ];
        },
        discountsColumnDefs() {
            return [
                {
                    field: 'title',
                    headerName: 'Discount Name',
                    autoWidthExclude: true,
                    minWidth: 150,
                },
                {
                    field: 'amount',
                    headerName: 'Amount',
                    cellRenderer: ({ data }) => {
                        if (data.dtype == 0) {
                            return data.value_display;
                        } else {
                            return data.modifiers_values_display.join('<br>');
                        }
                    },
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                    autoHeight: true,
                    sortable: false,
                },
                {
                    field: 'min_nights',
                    headerName: 'Min Nights',
                    cellRenderer: ({ data }) => {
                        if (data.dtype == 0) {
                            if (data.min_nights <= 1) {
                                return '-';
                            } else {
                                return data.min_nights;
                            }
                        } else {
                            return data.modifiers_min_nights_display.join(
                                '<br>'
                            );
                        }
                    },
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                    autoHeight: true,
                    sortable: false,
                },
                {
                    field: 'action',
                    headerName: 'Type',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                },
                {
                    field: 'automatic',
                    headerName: 'Automatic',
                    headerClass: 'ag-right-aligned-header',
                    cellClass: 'ag-right-aligned-cell',
                },
            ];
        },
        isMediumScreen() {
            const md = 768;
            return window.innerWidth >= md;
        },
    },
    methods: {
        async fetchAdditionalInfo() {
            try {
                this.$parent.$emit('busy', true);
                if (!this.additionalData) {
                    const data = await this.$store.dispatch(
                        'setupPropertyStore/getAdditionalData',
                        {
                            propertyId: this.propertyId,
                        }
                    );
                    this.additionalInfo = data;
                } else {
                    this.additionalInfo = this.additionalData;
                }
                this.isFetched = true;
            } catch (e) {
                this.errorToast({ text: 'Could not load additional info.' });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async saveRowOrder({ row, order, onSuccess, onError }) {
            try {
                onSuccess();
                this.$parent.$emit('busy', true);
                await API.post(
                    `${RENTAL_PROPERTY_SETUP_ADDITIONAL_ENDPOINT(
                        this.propertyId
                    )}set-service-order/`,
                    { service_id: row.id, order: order }
                );
                const data = await this.$store.dispatch(
                    'setupPropertyStore/getAdditionalData',
                    {
                        propertyId: this.propertyId,
                    }
                );
                this.additionalInfo = data;
            } catch (e) {
                this.errorToast({ text: 'Could not reorder services.' });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
:deep(.ag-icon-grip) {
    position: absolute;
    pointer-events: auto;
    top: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
}
@media (min-width: 768px) {
    :deep(.first-drag-column .ag-icon-grip) {
        position: relative;
        top: 5px;
        opacity: 1;
        margin-right: 5px;
    }
}

:deep(.ag-drag-handle.ag-row-drag) {
    margin-right: 0px;
}

.table-header {
    padding-top: 2px;
}
</style>